import React from 'react';
import { Link, type Href } from 'expo-router';
import { Platform, StyleSheet, Text, View } from 'react-native';
import { useHydratedWindowDimensions } from '../../hooks/useHydratedWindowDimensions';
import colors from '../../lib/colors';
import { interMedium, interRegular, interSemiBold } from '../../lib/fonts';
import { getDigestUrl } from '../../lib/paperUrl';
import type { PublicDigestData } from '../../lib/usePublicHomeDigests';
import { Body, Heading, PrimaryButton, Section, SectionLabel } from '../about/AboutPrimitives';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import type { LocalizedMessage } from '../../lib/i18n/messages';

// i18n-deferred-start: scientific-content -- illustrative feed taxonomy and paper titles
const FALLBACK_FEEDS = [
    {
        title: msg({ id: "home.homedigestexamples.visionAndMultimodalModels", message: "Vision and multimodal models", comment: "UI title in components/home/HomeDigestExamples.tsx" }),
        categories: 'Computer vision, machine learning, AI',
        href: '/public-digests',
        papers: [
            'Video grounding improves with lightweight VLM adapters',
            'Long-context multimodal reasoning benchmarks',
            'Evaluation traps in retrieval-augmented generation',
        ],
    },
    {
        title: msg({ id: "home.homedigestexamples.longCovidAndImmunology", message: "Long-Covid and immunology", comment: "UI title in components/home/HomeDigestExamples.tsx" }),
        categories: 'Medicine, immunology, public health',
        href: '/public-digests',
        papers: [
            'T-cell signatures linked to symptom clusters',
            'Post-viral dysautonomia review',
            'Early antiviral trial results in high-risk cohorts',
        ],
    },
    {
        title: msg({ id: "home.homedigestexamples.appliedAgentsAndToolUse", message: "Applied agents and tool use", comment: "UI title in components/home/HomeDigestExamples.tsx" }),
        categories: 'NLP, language models, automation',
        href: '/public-digests',
        papers: [
            'Structured tool-use with safety rails',
            'Latency-aware routing for production LLM systems',
            'Recurring research-data briefs from scoped sources',
        ],
    },
];
// i18n-deferred-end

interface HomeDigestExamplesProps {
    digests: PublicDigestData[] | null;
    fontsLoaded: boolean;
    targetCount: number;
}

type ExampleFeedCard = {
    title: LocalizedMessage;
    categories: string;
    papers: string[];
    projectId?: string;
    href: string;
};

export default function HomeDigestExamples({
    digests,
    fontsLoaded,
    targetCount,
}: HomeDigestExamplesProps) {
    const { i18n } = useLingui();
    const { width } = useHydratedWindowDimensions();
    const isNarrow = width < 760;
    const liveCards: ExampleFeedCard[] = (digests || []).map((digest) => ({
        title: digest.projectName,
        categories: digest.categories,
        projectId: digest.projectId,
        href: getDigestUrl(digest.digestShortId || digest.digestId, digest.projectName),
        papers: digest.papers.slice(0, 3).map((paper) => paper.title),
    }));
    const cardCount = Math.max(3, targetCount || 3);
    const fallbackCards: ExampleFeedCard[] = FALLBACK_FEEDS.map((feed) => ({
        title: feed.title,
        categories: feed.categories,
        href: feed.href,
        papers: feed.papers,
    }));
    const cards = [...liveCards, ...fallbackCards].slice(0, cardCount);
    const gridStyle = Platform.OS === 'web'
        ? ({ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20 } as any)
        : null;

    return (
        <Section top={80} bottom={80} isNarrow={isNarrow}>
            <SectionLabel fontsLoaded={fontsLoaded}><Trans id="home.homedigestexamples.exampleFeeds" comment="UI copy in components/home/HomeDigestExamples.tsx">Example feeds</Trans></SectionLabel>
            <Heading fontsLoaded={fontsLoaded}><Trans id="home.homedigestexamples.exampleResearchFeeds" comment="UI copy in components/home/HomeDigestExamples.tsx">Example research feeds</Trans></Heading>
            <View style={styles.leadCopy}>
                <Body fontsLoaded={fontsLoaded}><Trans id="home.homedigestexamples.exploreExampleFeedsToSeeHowPaperzillaTracksDifferentResearch" comment="UI copy in components/home/HomeDigestExamples.tsx">
                    Explore example feeds to see how Paperzilla tracks different research contexts, then use one as a starting point for your own topic. Browse the{' '}
                    <Link href="/public-digests" style={{ color: colors.primaryGreenDark, textDecorationLine: 'underline' }}>
                        public digests
                    </Link>
                    {' '}for more.
                </Trans>
                </Body>
            </View>

            <View style={[styles.feedGrid, gridStyle]}>
                {cards.map((feed) => (
                    <View key={`${typeof feed.title === 'string' ? feed.title : feed.title.id}-${feed.href}`} style={styles.feedCard}>
                        <Text style={[styles.feedMeta, fontsLoaded && interMedium]}>{feed.categories}</Text>
                        <Text style={[styles.feedTitle, fontsLoaded && interSemiBold]}>{typeof feed.title === 'string' ? feed.title : i18n._(feed.title)}</Text>
                        <View style={styles.paperList}>
                            {feed.papers.map((paper) => (
                                <View key={paper} style={styles.paperRow}>
                                    <View style={styles.paperDot} />
                                    <Text style={[styles.paperTitle, fontsLoaded && interRegular]}>{paper}</Text>
                                </View>
                            ))}
                        </View>
                        <View style={styles.feedFooter}>
                            <PrimaryButton
                                fontsLoaded={fontsLoaded}
                                href={((feed.projectId ? `/?openChat=true&templateProjectId=${feed.projectId}&entrySurface=home_example_feed` : feed.href) as Href)}
                                subdued
                            >
                                {feed.projectId ? <Trans id="home.homedigestexamples.subscribe" comment="Conditional UI copy in components/home/HomeDigestExamples.tsx">Subscribe</Trans> : <Trans id="home.homedigestexamples.browseExampleFeed" comment="Conditional UI copy in components/home/HomeDigestExamples.tsx">Browse example feed</Trans>}
                            </PrimaryButton>
                        </View>
                    </View>
                ))}
            </View>
        </Section>
    );
}

const styles = StyleSheet.create({
    leadCopy: {
        maxWidth: 760,
        marginBottom: 32,
    },
    feedGrid: {
        gap: 20,
    },
    feedCard: {
        backgroundColor: '#ffffff',
        borderColor: colors.border,
        borderRadius: 14,
        borderWidth: 1.5,
        color: colors.text900,
        minHeight: 320,
        paddingHorizontal: 22,
        paddingVertical: 24,
        ...(Platform.OS === 'web' && {
            textDecorationLine: 'none',
        }),
    },
    feedMeta: {
        color: colors.primaryGreen,
        fontSize: 12,
        fontWeight: '600',
        lineHeight: 17,
        marginBottom: 10,
    },
    feedTitle: {
        color: colors.text900,
        fontSize: 20,
        fontWeight: '700',
        lineHeight: 26,
        marginBottom: 18,
    },
    paperList: {
        gap: 10,
        marginBottom: 22,
    },
    paperRow: {
        alignItems: 'flex-start',
        backgroundColor: colors.bgWeak,
        borderRadius: 10,
        flexDirection: 'row',
        gap: 10,
        paddingHorizontal: 12,
        paddingVertical: 10,
    },
    paperDot: {
        backgroundColor: colors.primaryGreen,
        borderRadius: 999,
        height: 7,
        marginTop: 7,
        width: 7,
    },
    paperTitle: {
        color: colors.text700,
        flex: 1,
        fontSize: 14,
        lineHeight: 21,
    },
    feedFooter: {
        marginTop: 'auto',
    },
});
